/**
 * A surface's texture slots, every one through the same path: a gated reference loads, takes the
 * material's own texture state, and uploads in bands before it draws. The anisotropy flowmap takes
 * one extra step first, its repack. The result is keyed by Godot slot, which `materialBag.ts`
 * pairs with three's map names once.
 */
import { useMemo } from 'react';
import { useDisposeOnRelease } from '../useDisposeOnRelease';
import { useAnisotropicFilteringLevel } from '../contexts/AnisotropicFilteringContext';
import { NODE_PROPERTY, useTexture2DRid } from '../../resources/useTexture2D';
import { NO_RESOURCES } from '../SceneResourcesContext';
import { useTextureView } from '../useTextureView';
import { TEXTURE_SLOTS } from '../../resources/materials/standardmaterial3d/types';
import { isAnisotropic } from '../../resources/materials/standardmaterial3d/materialBag';
import { materialTextureStates, slotReadsSourceColor, slotViewSampler, topDownV, fromTopEdge, } from '../../resources/materials/standardmaterial3d/textureBinding';
import { repackAnisotropyFlowmap } from '../../resources/textures/repackFlowmap';
import { defaultWhiteTexture } from './defaultWhiteTexture';
import { pendingMapStandIn } from './pendingMapStandIn';
import { isLocalToScene } from '../../resources/textures/viewporttexture/localToScene.js';
/** The glTF surface a GLB override draws on, whose UVs keep Godot's top-down V (`godot/math/uv.ts`). */
export const GLTF_SURFACE = 'gltf';
/**
 * Every texture slot of one StandardMaterial3D, resolved in `material`'s own file's tables. Null
 * when a slot never draws, a file that cannot load or a ViewportTexture in a pass cycle: the
 * surface takes `MISSING_TEXTURE_MATERIAL`.
 */
export function useSurfaceTextures(scalars, material, surface) {
    // The decode's gated table, never the raw property bag: Godot samples a gated slot only inside
    // its `if (features[…])` branch (`scene/resources/material.cpp:1090,1745`), so a
    // `normal_texture` without `normal_enabled` reaches no shader.
    const references = scalars?.textureSlots;
    // Godot sets a slot's ViewportTexture up through each holder down to it, the material last.
    const isHeldLocally = material
        ? (material.isHeldLocally ?? true) && isLocalToScene(material.resource)
        : NODE_PROPERTY;
    const sources = useEachSlot(useSlotTexture, references, material, isHeldLocally);
    // The per-material half of every binding: UV set and transform, sampler filter and
    // wrapping. The per-slot half (which slots decode sRGB, which sample UV2) is `slotViewSampler`'s.
    const anisotropicFilteringLevel = useAnisotropicFilteringLevel();
    const states = useMemo(() => scalars && surfaceTextureStates(scalars, surface, anisotropicFilteringLevel), [scalars, surface, anisotropicFilteringLevel]);
    const flowmap = useRepackedFlowmap(sources.anisotropy_flowmap, scalars !== null && isAnisotropic(scalars));
    const drawn = useEachSlot(useDrawnSlot, { ...sources, anisotropy_flowmap: flowmap }, states);
    const maps = TEXTURE_SLOTS.map((slot) => drawn[slot]);
    const textures = useMemo(() => ({ ...drawn }), 
    // `maps` has one entry per slot, a fixed length, and stands for `drawn`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    maps);
    const isUnresolved = TEXTURE_SLOTS.some((slot) => sources[slot].missing);
    return isUnresolved ? null : textures;
}
/**
 * `useSlot(slot, ...args)` for each texture slot, in `TEXTURE_SLOTS` order, keyed by slot. The
 * loop answers `react-hooks/rules-of-hooks`: `TEXTURE_SLOTS` is a constant, so every render calls
 * the same hooks in the same order.
 */
function useEachSlot(useSlot, ...args) {
    const results = {};
    for (const slot of TEXTURE_SLOTS) {
        // eslint-disable-next-line react-hooks/rules-of-hooks -- `TEXTURE_SLOTS` is a constant.
        results[slot] = useSlot(slot, ...args);
    }
    return results;
}
/**
 * One slot's texture in every form a Texture2D slot takes, null while it arrives. A material binds
 * a texture by RID: an AtlasTexture's is its whole sheet (`scene/resources/atlas_texture.cpp:55-58`),
 * a CanvasTexture's names none, and a ViewportTexture's names its viewport's target.
 */
function useSlotTexture(slot, references, material, isHeldLocally) {
    const view = slotReadsSourceColor(slot) ? 'decoding' : 'plain';
    const result = useTexture2DRid(textureSlotReference(references, slot), material?.externalResources ?? NO_RESOURCES.externalResources, material?.internalResources ?? NO_RESOURCES.internalResources, view, isHeldLocally);
    return result.namesTexture ? result : { ...result, texture: defaultWhiteTexture() };
}
function textureSlotReference(references, slot) {
    const raw = references?.[slot];
    return typeof raw === 'string' ? raw : undefined;
}
/**
 * The flowmap as three reads it: its strength moved into blue. Only an anisotropic material
 * samples it, so only that one repacks (a canvas readback, a buffer copy and a GPU re-upload) and
 * waits for an arriving map. The memo depends on the two values the repack reads, not their wrapper.
 */
function useRepackedFlowmap(flowmap, isSampled) {
    const { texture } = flowmap;
    const repacked = useMemo(() => (isSampled && texture ? repackAnisotropyFlowmap(texture) : undefined), [isSampled, texture]);
    // This module allocated the repack, and no binding releases it.
    useDisposeOnRelease(repacked);
    return { texture: repacked ?? null, isArriving: flowmap.isArriving && isSampled };
}
/**
 * The map a slot draws: its texture as the material's texture state samples it, once on the GPU. A
 * large map uploads in bands and the slot keeps its old map meanwhile. With none, a stand-in fills
 * the slot while a map uploads or arrives (`pendingMapStandIn.ts`).
 */
function useDrawnSlot(slot, maps, states) {
    const { texture, isArriving } = maps[slot];
    // White samples the same under any wrap, filter, window or colour space, so a clone buys nothing.
    const isSampledAsIs = !texture || !states || texture === defaultWhiteTexture();
    const drawn = useTextureView(texture, isSampledAsIs ? null : slotViewSampler(texture, slot, states));
    if (drawn)
        return drawn;
    return texture || isArriving ? pendingMapStandIn(slot) : undefined;
}
/**
 * The texture states of `scalars` on `surface`. A UV1 attribute runs bottom-up on a Godot mesh and
 * top-down on a glTF surface. A triplanar set reads no attribute, and a UV2 state flips already, in
 * `materialTextureStates`.
 */
function surfaceTextureStates(scalars, surface, anisotropicFilteringLevel) {
    const states = materialTextureStates(scalars, anisotropicFilteringLevel);
    if (scalars.triplanar?.uv1)
        return states;
    return { ...states, uv1: surface === GLTF_SURFACE ? topDownV(states.uv1) : fromTopEdge(states.uv1) };
}
//# sourceMappingURL=surfaceTextures.js.map